import React, { useState, useRef } from 'react';
import { Mobile } from 'iconsax-react';
import { convertFarsiToEnglishNumerals } from '@/utils/numberConversionUtils';
import { useErrorStore } from '@/stores/profile/errorStore';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';

interface PhoneInputProps {
    onSubmit: (phone: string) => Promise<{ success: boolean; message: string }>;
    loading: boolean;
    title?: string;
    textBtn?: string;
    landingSlug?: string | undefined;
    newStyle?: boolean;
    popupWizardStyle?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
    onSubmit,
    loading,
    title,
    textBtn,
    newStyle,
    popupWizardStyle,
}) => {
    const [phone, setPhone] = useState('');
    const { setError, error } = useErrorStore();
    const { toast } = useToast();

    // Create a ref for the input
    const inputRef = useRef<HTMLInputElement | null>(null);

    const normalizePhoneNumber = (input: string) => {
        let convertedInput = convertFarsiToEnglishNumerals(input);
        convertedInput = convertedInput.replace(/\D/g, '');

        if (convertedInput.startsWith('98')) {
            convertedInput = convertedInput.slice(2);
        } else if (convertedInput.startsWith('0')) {
            // do nothing
        } else if (convertedInput.startsWith('9') && convertedInput.length === 10) {
            convertedInput = '0' + convertedInput;
        }
        return convertedInput;
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        const normalizedInput = normalizePhoneNumber(input);
        if (/^\d*$/.test(normalizedInput)) {
            setPhone(normalizedInput);
        }
    };

    const handleSubmit = async () => {
        if (error) {
            toast({
                title: error,
                duration: 5000,
                className: 'bg-red-500 bg-opacity-95 text-white rounded-2xl border-0',
            });
            inputRef.current?.focus();
            return;
        }

        if (phone.length === 11 && phone.startsWith('0')) {
            const { success, message } = await onSubmit(phone);

            toast({
                title: message,
                duration: 5000,
                className: success
                    ? 'bg-green-500 bg-opacity-95 text-white rounded-2xl border-0'
                    : 'bg-red-500 bg-opacity-95 text-white rounded-2xl border-0',
            });

            if (!success) {
                // Focus input on failure
                inputRef.current?.focus();
            }
        } else {
            const invalidMessage = 'شماره تماس باید ۱۱ رقم باشد';
            setError(invalidMessage);
            toast({
                title: invalidMessage,
                duration: 5000,
                className: 'bg-red-500 bg-opacity-95 text-white rounded-2xl border-0',
            });
            inputRef.current?.focus();
        }
    };

    if (popupWizardStyle) {
        return (
            <div className='w-full flex flex-col items-center select-none'>
                {title && title.trim() !== '' && (
                    <p className='text-center text-xs text-gray-500 dark:text-gray-400 mb-4 w-full'>
                        {title}
                    </p>
                )}
                <div
                    dir='rtl'
                    className='w-full flex items-center justify-between border border-[#EEAC43]/50 gap-1 focus-within:ring-2 focus-within:ring-[#EEAC43]/20 rounded-2xl p-1 bg-gray-100 dark:bg-white/5 transition-all'
                >
                    <Input
                        ref={inputRef}
                        className='flex-1 !bg-transparent rounded-xl border-none py-3 px-4 text-gray-900 dark:text-white focus-visible:ring-0 focus-visible:!ring-offset-0 shadow-none text-base font-medium relative outline-none text-left placeholder:text-right'
                        dir='ltr'
                        type='tel'
                        required
                        value={phone}
                        maxLength={11}
                        placeholder='شماره موبایل'
                        id='phone'
                        onChange={handlePhoneChange}
                        disabled={loading}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (!loading && phone.length === 11) {
                                    handleSubmit();
                                }
                            }
                        }}
                    />
                    <button
                        onClick={handleSubmit}
                        className={`bg-gradient-to-r from-[#e7c282] to-[#b48336] text-black hover:brightness-105 text-white py-2.5 px-5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
                            loading || phone.length < 11 ? 'opacity-40' : ''
                        }`}
                        disabled={loading || phone.length < 11}
                    >
                        {loading ? 'در حال ارسال ...' : (textBtn ?? 'ثبت نهایی')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={'flex flex-col items-start justify-center w-full'}>
            {title ? (
                <p className={`text-xl font-bold${title.length > 2 ? ' mb-5' : ''}`}>{title}</p>
            ) : (
                <>
                    <h3 className={'text-xl font-bold mb-2'}>ورود یا ثبت نام</h3>
                    <p
                        className={
                            'text-primary/70 dark:text-white/70 mb-6 text-xs max-lg:text-right'
                        }
                    >
                        با یک حساب کاربری از تمام خدمات خانه سرمایه استفاده کنید.
                    </p>
                </>
            )}

            <div
                className={`input-field border group border-secondary/10 w-full relative rounded-xl ${newStyle ? 'border-white/20' : 'bg-gray-100'}`}
            >
                {!newStyle && (
                    <Mobile
                        className='absolute top-1/2 transform -translate-y-1/2 right-1'
                        color={'#2d2c2c'}
                        size={20}
                    />
                )}

                <Input
                    ref={inputRef} // add ref here
                    className={`  pl-5 !bg-transparent border-none py-6 ${newStyle ? 'text-white px-5' : 'pr-9 px-2 text-black'}  z-10 focus-visible:outline-0 active:border-none active:outline-0 focus:border-none focus:outline-none focus-visible:border-none focus-visible:!ring-0 focus:ring-0 shadow-none !text-lg relative w-full text-left placeholder:text-right `}
                    dir='ltr'
                    type='tel'
                    required={true}
                    value={phone}
                    maxLength={11}
                    id='phone'
                    onChange={handlePhoneChange}
                    disabled={loading}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!loading && phone.length === 11) {
                                handleSubmit();
                            }
                        }
                    }}
                />

                <label
                    htmlFor='phone'
                    className={`absolute ${newStyle ? 'top-0 text-white/50 !bg-primary-active z-[2] px-2 right-3 ' : 'top-1/2 right-10  text-gray-500 dark:text-black/40'} transition-all duration-200 -translate-y-1/2 text-sm ${
                        phone && newStyle ? 'hidden' : ''
                    }`}
                >
                    شماره تماس
                </label>
            </div>
            <button
                onClick={handleSubmit}
                className={`mt-4 p-5 ${newStyle ? 'bg-[#EEAC43]' : 'bg-progress'} w-full text-white rounded-xl ${
                    loading || phone.length < 11 ? 'opacity-40' : ''
                }`}
                disabled={loading || phone.length < 11}
            >
                {loading ? 'در حال ارسال ...' : (textBtn ?? 'تکمیل ثبت نام')}
            </button>
        </div>
    );
};

export default PhoneInput;
